/**
 * waf-types.ts
 *
 * Shared WAF form types and hydration helpers used by WafTab and its
 * inner tab components. No JSX — safe for non-React consumers.
 */

// ── WAF config form model ───────────────────────────────────────────────────
export interface AiCfg {
  enabled: boolean;
  threshold: number;
  mode: string;
  ruleset_mode: string;
  ddos_sensitivity: number;
  threat_threshold: number;
  body_scan_limit: number;
  block_oversized_body: string;
  dlp_mode: string;
  graphql_max_depth: number;
  graphql_block_introspection: boolean;
  graphql_endpoints: string[];
  disabled_categories: string[];
  disabled_rules: string[];
  monitor_rules: string[];
}

export interface WafForm {
  enabled: boolean;
  mode: string;
  managed_rules_enabled: boolean;
  ai: AiCfg;
}

export const AI_DEFAULT: AiCfg = {
  enabled: false, threshold: 0.85, mode: 'detect', ruleset_mode: 'detect', ddos_sensitivity: 0,
  threat_threshold: 0, body_scan_limit: 64, block_oversized_body: 'auto', dlp_mode: '',
  graphql_max_depth: 0, graphql_block_introspection: false, graphql_endpoints: [],
  disabled_categories: [], disabled_rules: [], monitor_rules: [],
};

export const WAF_FORM_DEFAULT: WafForm = { enabled: false, mode: 'block', managed_rules_enabled: true, ai: { ...AI_DEFAULT } };

const N = (v: unknown, d: number) => (v == null ? d : Number(v));
const arr = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : []);

/** Deserialize a raw API config object into the typed WafForm model. */
export function hydrate(c: Record<string, unknown>): WafForm {
  const ai = (c.ai as Record<string, unknown>) || {};
  return {
    enabled: Boolean(c.enabled),
    mode: (c.mode as string) || 'block',
    managed_rules_enabled: c.managed_rules_enabled == null ? true : Boolean(c.managed_rules_enabled),
    ai: {
      enabled: Boolean(ai.enabled), threshold: N(ai.threshold, 0.85), mode: (ai.mode as string) || 'detect',
      ruleset_mode: (ai.ruleset_mode as string) || 'detect', ddos_sensitivity: N(ai.ddos_sensitivity, 0),
      threat_threshold: N(ai.threat_threshold, 0), body_scan_limit: N(ai.body_scan_limit, 64),
      block_oversized_body: (ai.block_oversized_body as string) || 'auto', dlp_mode: (ai.dlp_mode as string) || '',
      graphql_max_depth: N(ai.graphql_max_depth, 0), graphql_block_introspection: Boolean(ai.graphql_block_introspection),
      graphql_endpoints: arr(ai.graphql_endpoints), disabled_categories: arr(ai.disabled_categories),
      disabled_rules: arr(ai.disabled_rules), monitor_rules: arr(ai.monitor_rules),
    },
  };
}
